import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Skeleton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import {
  apiCallMediaVenueUser,
  apiCallDeleteMedia,
  apiCallClaimMediaApprove,
} from "../../../../Core/Apicall";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import DeleteIcon from "@mui/icons-material/Delete";
import ic_play from "../../../../assets/Images/ic_play.png";
import VenueMediaPhotoGallery from "../../../Common/ImageGallery/VenueMediaPhotoGallery.js";

type VenueMedia = {
  _id: string;
  venueId: string;
  mediaUrl: string;
  mediaType: string;
  uploadedByUser: boolean;
  approvalStatus: string;
  thumbnailMediaUrl: any;
  userId: string;
  fullName: string;
  createdAt?: string;
};

const VenueMediaUser: React.FC<{ venueId: string; venueName: string }> = ({
  venueId,
  venueName,
}) => {
  const [mediaList, setMediaList] = useState<VenueMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [openPreview, setOpenPreview] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(0);

  // const [selectedMediaForPreview, setSelectedMediaForPreview] =
  //   useState<null | { type: string; url: string }>(null);
  const [selectedMediaForDelete, setSelectedMediaForDelete] =
    useState<VenueMedia | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const userdata: any = useSelector((state: RootState) => state.global.token);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(50);
  const [status, setStatus] = useState("Verified");

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedMediaForStatus, setSelectedMediaForStatus] =
    useState<VenueMedia | null>(null);

  // ✅ Fetch Media
  const fetchMedia = async (nextPage: number, value: string) => {
    try {
      setLoading(true);
      const data = { venueId, offset: 1, limit: nextPage, filter: value };
      const res = await apiCallMediaVenueUser(data);
      setTotalCount(res?.totalCount);
      if (res.data) setMediaList(res.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete Logic
  const handleDeleteClick = (media: VenueMedia) => {
    setSelectedMediaForDelete(media);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedMediaForDelete) return;
    try {
      await apiCallDeleteMedia({
        adminId: userdata?.data?.userId,
        venueMediaId: selectedMediaForDelete._id,
        isDelete: true,
      });
      setMediaList((prev) =>
        prev.filter((item) => item._id !== selectedMediaForDelete._id)
      );
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedMediaForDelete(null);
    }
  };

  // ✅ Status Change → open confirmation
  const handleStatusChange = (media: VenueMedia, newStatus: string) => {
    setSelectedMediaForStatus(media);
    setSelectedStatus(newStatus);
    setStatusDialogOpen(true);
  };

  // ✅ Confirmed change
  const handleConfirmStatusChange = async () => {
    if (!selectedMediaForStatus || !selectedStatus) return;

    try {
      await apiCallClaimMediaApprove({
        userId: userdata?.data?.userId,
        venueMediaId: selectedMediaForStatus._id,
        approvalStatus: selectedStatus,
      });

      // Update locally
      setMediaList((prev) =>
        prev.map((item) =>
          item._id === selectedMediaForStatus._id
            ? { ...item, approvalStatus: selectedStatus }
            : item
        )
      );

      // ✅ Refresh data from API (for accuracy)
      await fetchMedia(page, status);
    } catch (err) {
      console.error("Status update failed:", err);
    } finally {
      setStatusDialogOpen(false);
      setSelectedStatus(null);
      setSelectedMediaForStatus(null);
    }
  };

  useEffect(() => {
    fetchMedia(page, status);
  }, [venueId, page, status]);

  // ✅ Dynamic status options (don’t show current one)
  const getStatusOptions = (current: string) => {
    const all = ["UnderApproval", "Verified", "Rejected"];
    return all.filter((s) => s !== current);
  };  
  return (
    <>
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        p={2}
        gap={2}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={status}
            label="Status Filter"
            onChange={(e) => {
              setPage(50);
              setStatus(e.target.value);
              fetchMedia(50, e.target.value);
            }}
          >
            <MenuItem value="Verified">Verified</MenuItem>
            <MenuItem value="UnderApproval">Under Approval</MenuItem>
            <MenuItem value="Rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 1.8,
            p: 2,
            width: "100%",
          }}
        >
          {Array.from(new Array(8)).map((_, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                boxShadow: 2,
                height: 210,
              }}
            >
              <Skeleton variant="rectangular" height={150} animation="wave" />
              <Box sx={{ p: 1 }}>
                <Skeleton width="70%" height={16} animation="wave" />
                <Skeleton width="50%" height={13} animation="wave" />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
          gap: 1.8,
          p: 2,
          width: "100%",
        }}
      >
        {mediaList.map((media) => (
          <Box
            key={media._id}
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: 2,
              transition: "all 0.25s ease",
              "&:hover": { transform: "scale(1.03)" },
            }}
          >
            {/* ✅ Preview */}
            {media.mediaType === "Image" ? (
              <Avatar
                onClick={() => {
                  const idx = mediaList.findIndex((m) => m._id === media._id);
                  setSelectedIndex(idx); // set clicked index
                  setOpenPreview(true); // open gallery
                }}
                src={media.mediaUrl}
                variant="rounded"
                sx={{ width: "100%", height: 150, cursor: "pointer" }}
              />
            ) : media.mediaType === "Video" ? (
              <>
                <Avatar
                  onClick={() => {
                    const idx = mediaList.findIndex((m) => m._id === media._id);
                    setSelectedIndex(idx); // set clicked index
                    setOpenPreview(true); // open gallery
                  }}
                  src={media.thumbnailMediaUrl}
                  variant="rounded"
                  sx={{ width: "100%", height: 150, cursor: "pointer" }}
                />
                <Box
                  onClick={() =>
                    // setSelectedMediaForPreview({
                    //   type: "Video",
                    //   url: media.mediaUrl,
                    // })
                    setOpenPreview(true)
                  }
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 40,
                    height: 40,
                    cursor: "pointer",
                    borderRadius: "50%",
                    backgroundColor: "rgba(0,0,0,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={ic_play}
                    alt="Play"
                    style={{
                      width: 20,
                      height: 20,
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </>
            ) : (
              <></>
            )}

            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                bgcolor: "rgba(0,0,0,0.6)",
                color: "white",
                p: 0.7,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.75rem",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                >
                  {media.fullName || "Unknown User"}
                </Typography>
                {media.createdAt && (
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {new Date(media.createdAt).toLocaleDateString()}
                  </Typography>
                )}
              </Box>

              <Select
                size="small"
                value={media.approvalStatus}
                onChange={(e) =>
                  handleStatusChange(media, e.target.value as string)
                }
                renderValue={() => {
                  if (media.approvalStatus === "Verified")
                    return <CheckCircleIcon sx={{ color: "#4caf50" }} />;
                  if (media.approvalStatus === "Rejected")
                    return <CancelIcon sx={{ color: "#f44336" }} />;
                  return <DoNotDisturbOnIcon sx={{ color: "#ff9800" }} />;
                }}
                sx={{
                  minWidth: 36,
                  height: 36,
                  borderRadius: 1,
                  bgcolor: "rgba(255,255,255,0.08)",
                  "& .MuiSelect-select": {
                    p: 1,
                    display: "flex",
                    justifyContent: "center",
                  },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiSelect-icon": {
                    right: 1,
                    color:
                      media.approvalStatus == "Verified"
                        ? "#4caf50"
                        : media.approvalStatus == "Rejected"
                        ? "#f44336"
                        : "#ff9800",
                  },
                }}
              >
                {getStatusOptions(media.approvalStatus).map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "UnderApproval" && (
                      <DoNotDisturbOnIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "#ff9800" }}
                      />
                    )}
                    {option === "Verified" && (
                      <CheckCircleIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "#4caf50" }}
                      />
                    )}
                    {option === "Rejected" && (
                      <CancelIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "#f44336" }}
                      />
                    )}
                    {option == "UnderApproval" ? "Under Approval" : option}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* ✅ Delete */}
            <IconButton
              size="small"
              sx={{
                position: "absolute",
                top: 5,
                right: 5,
                bgcolor: "rgba(255,255,255,0.8)",
                "&:hover": { bgcolor: "rgba(255,0,0,0.2)" },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(media);
              }}
            >
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </Box>
        ))}

        {mediaList.length === 0 && !loading && (
          <Box
            gridColumn="1 / -1"
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100px"
          >
            <Typography color="textSecondary" fontWeight="bold">
              Data not found
            </Typography>
          </Box>
        )}
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Are you sure you want to delete this media?</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Status Confirm Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
      >
        <DialogTitle>
          Are you sure you want to change the status to{" "}
          <strong>{selectedStatus}</strong>?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmStatusChange}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Preview Dialog */}
      {/* <Dialog
        open={!!selectedMediaForPreview}
        onClose={() => setSelectedMediaForPreview(null)}
      >
        {selectedMediaForPreview?.type === "Image" ? (
          <Avatar
            src={selectedMediaForPreview.url}
            variant="rounded"
            sx={{ width: 320, height: "auto" }}
          />
        ) : selectedMediaForPreview?.type === "Video" ? (
          <video controls autoPlay loop>
            <source src={selectedMediaForPreview.url} type="video/mp4" />
          </video>
        ) : null}
      </Dialog> */}

      {/* ✅ View More */}
      {page < totalCount && (
        <Box display="flex" justifyContent="center" mt={1.5} pb={2}>
          <Typography
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "#1976d2",
              fontWeight: 500,
              fontSize: "0.9rem",
            }}
            onClick={() => {
              const nextPage = page + 10;
              setPage(nextPage);
              fetchMedia(nextPage, status);
            }}
          >
            View More
          </Typography>
        </Box>
      )}
      {openPreview && (
        <VenueMediaPhotoGallery
          open={openPreview}
          venueName={venueName ?? "Venue"}
          mediaList={mediaList}
          selectedIndex={selectedIndex}
          onClose={() => setOpenPreview(false)}
        />
      )}
    </>
  );
};

export default VenueMediaUser;
